import { Controller } from "@hotwired/stimulus";

/**
 * Handles hover card display for tables on the floor plan
 * Moves card to body to escape overflow containers and transforms
 *
 * Note: We store a direct reference to the card element because
 * Stimulus targets are scoped to the controller's element, and
 * moving the card to body makes hasCardTarget return false.
 * We also manually bind event listeners since Stimulus actions
 * won't work once the card is outside the controller's DOM tree.
 */
export default class extends Controller {
  static targets = ["card"];

  connect() {
    this.hideTimeout = null;
    this.originalParent = null;
    // Store direct reference since target won't work after moving to body
    this.card = this.hasCardTarget ? this.cardTarget : null;

    // Manually bind event listeners since Stimulus actions won't work
    // once card is moved to body (outside controller's DOM tree)
    if (this.card) {
      this.boundCardEnter = this.cardEnter.bind(this);
      this.boundCardLeave = this.cardLeave.bind(this);
      this.card.addEventListener("mouseenter", this.boundCardEnter);
      this.card.addEventListener("mouseleave", this.boundCardLeave);
    }
  }

  disconnect() {
    this.clearHideTimeout();
    if (this.card) {
      this.card.removeEventListener("mouseenter", this.boundCardEnter);
      this.card.removeEventListener("mouseleave", this.boundCardLeave);
    }
    this.returnCardToParent();
  }

  show() {
    this.clearHideTimeout();
    // Hide all other hover cards immediately
    document.querySelectorAll("[data-table-hover-target='card']").forEach((card) => {
      if (card !== this.card) {
        card.classList.add("hidden");
      }
    });
    if (this.card) {
      this.moveCardToBody();
      this.card.classList.remove("hidden");
      this.positionCard();
    }
  }

  hide() {
    this.hideTimeout = setTimeout(() => {
      if (this.card) {
        this.card.classList.add("hidden");
        this.returnCardToParent();
      }
    }, 150);
  }

  moveCardToBody() {
    if (!this.card) return;
    if (this.card.parentElement === document.body) return;

    this.originalParent = this.card.parentElement;
    document.body.appendChild(this.card);
  }

  returnCardToParent() {
    if (!this.card || !this.originalParent) return;
    if (this.card.parentElement === this.originalParent) return;

    this.originalParent.appendChild(this.card);
    this.originalParent = null;
  }

  cardEnter() {
    this.clearHideTimeout();
  }

  cardLeave() {
    this.hide();
  }

  positionCard() {
    if (!this.card) return;

    const rect = this.element.getBoundingClientRect();
    const cardWidth = 288; // w-72 = 18rem = 288px
    const gap = 8;

    // Determine horizontal position (prefer right of element)
    let left;
    if (rect.right + cardWidth + gap < window.innerWidth) {
      left = rect.right + gap;
    } else {
      left = rect.left - cardWidth - gap;
    }

    // Determine vertical position (prefer aligned to top)
    let top = rect.top;

    // Ensure card doesn't go off bottom of screen
    this.card.style.maxHeight = `${window.innerHeight - 32}px`;
    this.card.style.overflowY = "auto";

    // Adjust if card would go off bottom
    const estimatedHeight = 400;
    if (top + estimatedHeight > window.innerHeight) {
      top = Math.max(16, window.innerHeight - estimatedHeight - 16);
    }

    this.card.style.left = `${left}px`;
    this.card.style.top = `${top}px`;
  }

  clearHideTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
